import { css, keyframes } from '@emotion/react';
import { Box, Flex, Grid } from 'components/box';
import Button from 'components/button';
import { useCustomer } from 'hooks/customer';
import { useState, useEffect, useCallback } from 'react';
import RenderControl from '../render-control';
import { Text } from 'components/typography';
import { X as XIcon } from 'react-feather';
import {
  getItemFromLocalStorage,
  LocalStorageKeys,
  setItemInLocalStorage,
} from '../../../../util/local-storage';
import { daysSinceTimestamp } from '../../../../util/date';
import { gradientConfettiPalette } from 'themes/onedayonly';
import LinearGradientBorder from 'components/_shared/elements/linear-gradient-border';
import { Link } from 'components/configurable-routing';

const TOAST_BOTTOM_SHOWN = '30px';
const TOAST_BOTTOM_HIDDEN = '-100px';
const TOAST_ANIMATION_DURATION = 500;
const TOAST_DELAY_IN_MONTHS = 3;

const slideUp = keyframes`
  from {
    bottom: ${TOAST_BOTTOM_HIDDEN};
  }
  to {
    bottom: ${TOAST_BOTTOM_SHOWN};
  }
`;

const slideDown = keyframes`
  from {
    bottom: ${TOAST_BOTTOM_SHOWN};
  }
  to {
    bottom: ${TOAST_BOTTOM_HIDDEN};
  }
`;

const AmountSavedToast = () => {
  const customer = useCustomer();
  const [isVisible, setIsVisible] = useState(false);
  const [isRendered, setIsRendered] = useState(false);

  const open = useCallback(() => {
    const lastShown = getItemFromLocalStorage(
      LocalStorageKeys.TimeSinceAmountSavedToast
    );
    const daysSinceLastShown = lastShown
      ? daysSinceTimestamp(lastShown)
      : undefined;

    if (
      typeof daysSinceLastShown === 'number' &&
      daysSinceLastShown < 28 * TOAST_DELAY_IN_MONTHS
    ) {
      return;
    }
    setItemInLocalStorage(
      LocalStorageKeys.TimeSinceAmountSavedToast,
      new Date().toISOString()
    );
    setIsRendered(true);
    setIsVisible(true);
  }, []);

  const onClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      setIsRendered(false);
    }, TOAST_ANIMATION_DURATION);
  };

  useEffect(() => {
    if (
      customer &&
      customer?.orderStats?.saving?.formattedValue &&
      customer.orderStats?.saving?.value > 0
    ) {
      open();
    }
  }, [customer, open]);

  if (
    !isRendered ||
    !customer?.orderStats?.saving?.formattedValue ||
    !customer.orderStats?.saving?.value
  ) {
    return null;
  }

  return (
    <Box
      borderRadius={2}
      width={['90vw', null, '60vw']}
      backgroundColor="white"
      left="50%"
      zIndex={1000}
      p={1}
      css={theme => css`
        position: fixed;
        transform: translateX(-50%);
        animation: ${isVisible ? slideUp : slideDown}
          ${TOAST_ANIMATION_DURATION}ms forwards;
        box-shadow: ${theme.shadows.medium};
      `}
    >
      <LinearGradientBorder
        to="bottom"
        startColor={gradientConfettiPalette.amountSaved.start}
        endColor={gradientConfettiPalette.amountSaved.end}
      >
        <Grid gridTemplateColumns="auto 1fr auto" p={2} width="100%">
          <Flex
            width="48px"
            height="48px"
            borderRadius="50%"
            css={css`
              background: linear-gradient(
                180deg,
                rgba(255, 194, 34, 0.0384) 0%,
                rgba(255, 194, 34, 0.16) 100%
              );
            `}
            justifyContent="center"
            alignItems="center"
            fontSize={5}
          >
            💰
          </Flex>
          <Link
            href="/my-account"
            color="unset"
            height="100%"
            fontWeight="normal"
          >
            <Flex
              flexDirection="column"
              justifyContent="space-between"
              py={2}
              css={css`
                gap: 4px;
              `}
            >
              <Text>
                You saved{' '}
                <strong>{customer.orderStats.saving.formattedValue}</strong> so
                far!
              </Text>
              <Text fontSize={1}>Click to view your savings</Text>
            </Flex>
          </Link>
          <Button
            variant="minimal"
            onClick={onClose}
            width="48px"
            height="48px"
          >
            <Box
              my="auto"
              backgroundColor="shopGrey"
              width="24px"
              height="24px"
              borderRadius="50px"
              p={1}
            >
              <XIcon color="black" size={16} />
            </Box>
          </Button>
        </Grid>
      </LinearGradientBorder>
    </Box>
  );
};

const Wrapper = () => (
  <RenderControl type="client">
    <AmountSavedToast />
  </RenderControl>
);

export default Wrapper;
